import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Alert, Chip, Stack, Stepper, Step, StepLabel, TextField, Grid, Divider,
  CircularProgress, Paper,
} from '@mui/material';
import { Verified, Send, LocalShipping, Code } from '@mui/icons-material';
import { eInvoiceService, EInvoiceDocument, EWayBill } from '../../services/eInvoiceService';

const STEPS = ['Draft', 'Validated', 'Ready', 'Submitted'];
const stepIndex = (status?: string) => {
  switch (status) {
    case 'VALIDATED': return 1;
    case 'READY': return 2;
    case 'SUBMITTED': return 3;
    default: return 0;
  }
};

interface Props {
  open: boolean;
  invoiceId: number | null;
  invoiceNumber?: string;
  onClose: () => void;
}

const EInvoiceDialog: React.FC<Props> = ({ open, invoiceId, invoiceNumber, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [einvoice, setEinvoice] = useState<EInvoiceDocument | null>(null);
  const [eway, setEway] = useState<EWayBill | null>(null);
  const [payload, setPayload] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showPayload, setShowPayload] = useState(false);
  const [ewayForm, setEwayForm] = useState({ transporter: '', vehicleNo: '', distanceKm: '' });

  const load = useCallback(() => {
    if (!invoiceId) return;
    setLoading(true);
    Promise.all([eInvoiceService.get(invoiceId), eInvoiceService.preview(invoiceId)])
      .then(([g, p]) => {
        setEinvoice(g.eInvoice); setEway(g.eWayBill);
        setPayload(p.payload); setValidationErrors(p.errors || []);
      })
      .catch((e) => setError(e.message || 'Failed to load e-invoice status'))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  useEffect(() => { if (open) { setError(null); setShowPayload(false); load(); } }, [open, load]);

  const doValidate = async () => {
    if (!invoiceId) return;
    setBusy(true); setError(null);
    try {
      const r = await eInvoiceService.validate(invoiceId);
      setValidationErrors(r.errors || []);
      load();
    } catch (e: any) { setError(e.message || 'Validation failed'); }
    finally { setBusy(false); }
  };

  const doSubmit = async () => {
    if (!invoiceId) return;
    setBusy(true); setError(null);
    try { await eInvoiceService.submit(invoiceId); load(); }
    catch (e: any) { setError(e.message || 'Submission failed'); }
    finally { setBusy(false); }
  };

  const doEway = async () => {
    if (!invoiceId) return;
    setBusy(true); setError(null);
    try {
      await eInvoiceService.generateEWay(invoiceId, {
        transporter: ewayForm.transporter || undefined,
        vehicleNo: ewayForm.vehicleNo || undefined,
        distanceKm: ewayForm.distanceKm ? Number(ewayForm.distanceKm) : undefined,
      });
      load();
    } catch (e: any) { setError(e.message || 'Failed to prepare e-way bill'); }
    finally { setBusy(false); }
  };

  const status = einvoice?.status || 'DRAFT';
  const canSubmit = status === 'READY' || status === 'VALIDATED';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        E-Invoice & E-Way Bill {invoiceNumber ? `· ${invoiceNumber}` : ''}
        <Chip label="DEMO MODE" color="warning" size="small" sx={{ ml: 1 }} />
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Integration-ready: the real IRP payload is built and validated below. No live ClearTax / NIC credentials
          are configured, so <b>no government IRN or e-way-bill number is generated</b>. Configure a GSP in
          Integrations to go live.
        </Alert>

        {loading ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box> : (
          <>
            <Stepper activeStep={stepIndex(status)} sx={{ mb: 2 }}>
              {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
            </Stepper>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {validationErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Validation issues</Typography>
                <ul style={{ margin: '4px 0 0 16px' }}>{validationErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" startIcon={<Verified />} onClick={doValidate} disabled={busy}>Validate</Button>
              <Button variant="contained" startIcon={<Send />} onClick={doSubmit} disabled={busy || !canSubmit}>Submit (Demo)</Button>
              <Button variant="text" startIcon={<Code />} onClick={() => setShowPayload((v) => !v)}>{showPayload ? 'Hide' : 'View'} IRP payload</Button>
            </Stack>

            {einvoice?.status === 'SUBMITTED' && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Submitted in DEMO mode. IRN / Ack No / QR are intentionally blank — they are only issued by the live IRP.
              </Alert>
            )}

            {showPayload && payload && (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, maxHeight: 260, overflow: 'auto', bgcolor: '#0f172a' }}>
                <pre style={{ margin: 0, color: '#e2e8f0', fontSize: 12 }}>{JSON.stringify(payload, null, 2)}</pre>
              </Paper>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShipping fontSize="small" /> E-Way Bill
            </Typography>
            {eway ? (
              <Alert severity="success" sx={{ mb: 1 }}>
                Prepared (DEMO) · Transporter: {eway.transporter || '—'} · Vehicle: {eway.vehicle_no || '—'} ·
                Valid till: {eway.valid_until ? String(eway.valid_until).slice(0, 10) : '—'} · EWB No: <b>{eway.ewb_no || 'not issued (demo)'}</b>
              </Alert>
            ) : (
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid size={{ xs: 12, sm: 5 }}><TextField size="small" fullWidth label="Transporter" value={ewayForm.transporter} onChange={(e) => setEwayForm({ ...ewayForm, transporter: e.target.value })} /></Grid>
                <Grid size={{ xs: 7, sm: 4 }}><TextField size="small" fullWidth label="Vehicle No" value={ewayForm.vehicleNo} onChange={(e) => setEwayForm({ ...ewayForm, vehicleNo: e.target.value })} /></Grid>
                <Grid size={{ xs: 5, sm: 3 }}><TextField size="small" fullWidth type="number" label="Distance (km)" value={ewayForm.distanceKm} onChange={(e) => setEwayForm({ ...ewayForm, distanceKm: e.target.value })} /></Grid>
                <Grid size={{ xs: 12 }}><Button variant="outlined" startIcon={<LocalShipping />} onClick={doEway} disabled={busy}>Prepare E-Way Bill (Demo)</Button></Grid>
              </Grid>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EInvoiceDialog;
